import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import './Thumbnail.css';
import { PromotionStateIndicator } from '../PromotionStateIndicator';
import { StringUtilService } from '../../../../../../shared/src/services/util/string-util.service';
import { useTranslations } from '../../../../../../shared/src/hooks/use-translations.hook';
import { PromotionCta } from '../PromotionCta';
import { PromotionsService } from '../../../../../../shared/src/services/promotions.service';
import { ltPromotionStatusesEnum } from '../../../../../../shared/src/enums/promotions.enum';
import { useClampTextToRows } from '../../../../../../shared/src/hooks/use-clamp-text-to-rows.hook';
import { useAccessibleClick } from '../../../../../../shared/src/hooks/use-accessible-click.hook';

export const Thumbnail = ({
    className = '',
    promotion,
    // size small|big
    size = 'small',
}) => {
    const {
        slug,
        images,
        scheduleIso,
        content,
        colors,
        actions,
        orientation,
    } = promotion;
    const { session } = useSelector((state) => state.player);
    const { t } = useTranslations();
    const promotionsBaseSlug = 'promotions';
    const promotionPath = `/${promotionsBaseSlug}/${slug}`;
    const tncRef = useRef(null);
    const regulation = useSelector((state) => state.app.regulation);
    const handleKeyDown = useAccessibleClick(() => {
        window.location.href = promotionPath;
    }, { preventDefault: false });
    const tncClampEnding = (`
        <a
            href="${promotionPath}"
            class="asg-promotion-fold-thumbnail__link"
        >
            <span class="asg-promotion-fold-thumbnail__link-dots">...</span>${t('readMore')}
        </a>
    `);

    const isFullyVisible = () => {
        return regulation === 'MC';
    };

    useClampTextToRows(
        isFullyVisible() ? null : tncRef,
        2,
        tncClampEnding,
    );

    const colorTheme = colors.colorTheme;
    const colorVars = PromotionsService.getFoldColorsVariables(colors);
    const promotionStatus = PromotionsService.getPromotionsStatus(scheduleIso);
    const isShownCountdown = content.showCountdown;
    const isShowStateIndicator = PromotionsService.isShowStateIndicator(promotionStatus, isShownCountdown);

    // sanitized content
    const titleSanitized = StringUtilService.sanitizeHtml(content.title);
    const subTitleSanitized = StringUtilService.sanitizeHtml(content.subtitle);
    const ctaTextSanitized = StringUtilService.sanitizeHtml(content.ctaText);
    const termsSanitized = StringUtilService.sanitizeHtml(content.terms);
    const countdownHeadlineSanitized = StringUtilService.sanitizeHtml(content.countdownHeadline);

    const thumbnailClasses = [
        className,
        'asg-promotion-fold-thumbnail',
        `asg-promotion-fold-thumbnail--${size}`,
        `asg-promotion-fold-thumbnail--${promotionStatus}`,
        `asg-promotion-fold-thumbnail--${colorTheme}`,
        PromotionsService.isCountdownState(promotionStatus) && isShownCountdown
            ? 'asg-promotion-fold-thumbnail--countdown'
            : '',
    ];

    return (
        <div className={thumbnailClasses.join(' ')} style={colorVars}>
            <a href={promotionPath} className="asg-promotion-fold-thumbnail__img-link">
                <img
                    className="asg-promotion-fold-thumbnail__img asg-promotion-fold-thumbnail__img--mobile"
                    src={images.mobile}
                    alt={titleSanitized}
                    loading="lazy"
                />
                <img
                    className="asg-promotion-fold-thumbnail__img asg-promotion-fold-thumbnail__img--desktop"
                    src={images.web}
                    alt={titleSanitized}
                    loading="lazy"
                />
            </a>
            <div className="asg-promotion-fold-thumbnail__content">
                {isShowStateIndicator && <PromotionStateIndicator
                    schedule={scheduleIso}
                    clockTitle={countdownHeadlineSanitized}
                />}

                <div className="asg-promotion-fold-thumbnail__details">
                    {titleSanitized && <a
                        href={promotionPath}
                        className="asg-promotion-fold-thumbnail__details-title"
                        dangerouslySetInnerHTML={{ __html: titleSanitized }}
                    />}
                    {subTitleSanitized && <a
                        href={promotionPath}
                        className="asg-promotion-fold-thumbnail__details-subtitle"
                        dangerouslySetInnerHTML={{ __html: subTitleSanitized }}
                    />}

                    {promotionStatus !== ltPromotionStatusesEnum.EXPIRED && <PromotionCta
                        ctaText={ctaTextSanitized}
                        ctaActionObj={!session ? actions.preLogin : actions.postLogin}
                        orientation={orientation}
                    />}

                    <div className="asg-promotion-fold-thumbnail__details-tnc">
                        {content.terms && (
                            <>
                                <div
                                    className="asg-promotion-fold-thumbnail__details-tnc-title"
                                >{t('termsAndConditionsPROMOTION')}</div>
                                <div
                                    tabIndex="0"
                                    onKeyDown={handleKeyDown}
                                    onClick={() => window.location.href = promotionPath}
                                    className="asg-promotion-fold-thumbnail__details-tnc-text"
                                    dangerouslySetInnerHTML={{ __html: termsSanitized }}
                                    ref={tncRef}
                                />
                            </>
                        )}
                        {!content.terms && (
                            <a
                                href={promotionPath}
                                className="asg-promotion-fold-thumbnail__link
                                asg-promotion-fold-thumbnail__link--full-tnc"
                            >{t('fullTermsAndConditions')}</a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
